/* ============================================================
   Velvet Rituals — AI Partner live chat demo
   assets/vr-ai-chat.js

   Injected by sections/vr-ai-partners.liquid once the partners
   section scrolls into view, so it costs the page nothing at
   load but is always resident before the first click.

   Talks to the Velvet gateway as an anonymous guest. The message
   allowance is enforced server-side (403 GUEST_LIMIT_REACHED);
   the counter shown in the UI only mirrors it.

   1. Guest session      — mint + cache, re-mint once on 401
   2. Room open / close  — FLIP the chosen card out and back, the
                           panel unfurling and folding on the mirror
                           of the same choreography
   3. Conversation       — optimistic send, paced reveal
   4. Vibration          — tap-gated per reply, never auto-fired
   5. Gates              — the wall, image asks, refusals
   ============================================================ */
(function () {
  'use strict';
  if (window.__vrAiChatInit) return;
  window.__vrAiChatInit = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOK_KEY = 'vrai.tok';
  var WALL_KEY = 'vrai.walled';

  /* Vibration preview ceiling. The score the API returns runs ~40s;
     that is a device session, not a demo. */
  var BUZZ_MS = 4000;

  /* patternId → shape, from GET /connect/built-in-patterns */
  var SHAPES = {
    1: 'constant', 2: 'slow', 3: 'fast', 4: 'wave',
    6: 'step', 7: 'random', 8: 'heart'
  };

  function store(k, v) {
    try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch (e) {}
  }
  function recall(k) {
    try { return localStorage.getItem(k); } catch (e) { return null; }
  }

  /* ── 1. Guest session ──────────────────────────────── */
  function tokenAlive(raw) {
    if (!raw) return false;
    try {
      var body = JSON.parse(atob(raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return body.exp && body.exp * 1000 > Date.now() + 60000;
    } catch (e) { return false; }
  }

  function Session(cfg) {
    this.cfg = cfg;
    this.token = null;
    this.pending = null;
  }

  Session.prototype.headers = function () {
    return {
      'Content-Type': 'application/json',
      'application-type-id': String(this.cfg.appType),
      'Authorization': 'Bearer ' + this.token
    };
  };

  Session.prototype.ready = function () {
    var self = this;
    if (this.token && tokenAlive(this.token)) return Promise.resolve(this.token);

    var cached = recall(TOK_KEY);
    if (tokenAlive(cached)) { this.token = cached; return Promise.resolve(cached); }

    if (this.pending) return this.pending;
    this.pending = fetch(this.cfg.api + '/auth/guest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ applicationTypeId: self.cfg.appType })
    }).then(function (r) {
      if (!r.ok) throw new Error('guest ' + r.status);
      return r.json();
    }).then(function (j) {
      var t = j && j.data && j.data.accessToken;
      if (!t) throw new Error('no token');
      self.token = t;
      store(TOK_KEY, t);
      store(WALL_KEY, null);
      self.pending = null;
      return t;
    }).catch(function (e) {
      self.pending = null;
      throw e;
    });
    return this.pending;
  };

  /* Any gateway call, with one automatic re-mint on 401. */
  Session.prototype.call = function (path, opts, retried) {
    var self = this;
    opts = opts || {};
    return this.ready().then(function () {
      return fetch(self.cfg.api + path, {
        method: opts.method || 'GET',
        headers: self.headers(),
        body: opts.body ? JSON.stringify(opts.body) : undefined
      });
    }).then(function (r) {
      if (r.status === 401 && !retried) {
        self.token = null;
        store(TOK_KEY, null);
        return self.call(path, opts, true);
      }
      return r.json().catch(function () { return {}; }).then(function (j) {
        return { status: r.status, ok: r.ok, body: j };
      });
    });
  };

  /* ── Reply hygiene ─────────────────────────────────── */
  /* The model leaks its own direction cues into the text. The
     mobile client strips the same two keys. */
  function clean(text) {
    return String(text || '')
      .replace(/spiciness:\s*\d+/gi, '')
      .replace(/duration_seconds:\s*\d+/gi, '')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/https?:\/\/\S*blob\.core\.windows\.net\/\S*/gi, '')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  }

  /* A reply of two or more sentences reads as one block if it lands
     at once. People send a thought, then add to it. */
  function splitTurns(text) {
    if (text.length < 70) return [text];
    var m = text.match(/^([\s\S]{40,}?[.!?…])\s+([\s\S]+)$/);
    return m ? [m[1].trim(), m[2].trim()] : [text];
  }

  /* ── 4. Vibration ──────────────────────────────────── */
  /* Desktop Chrome defines navigator.vibrate and then does nothing with
     it, so presence alone would offer a switch that cannot work. Only
     a touch device gets the affordance. iOS Safari has no API at all. */
  var CAN_BUZZ = typeof navigator !== 'undefined' &&
                 typeof navigator.vibrate === 'function' &&
                 window.matchMedia('(pointer: coarse)').matches;

  /* The web has no amplitude control — vibration is on or off. So
     intensity becomes duty cycle: a higher number buzzes for more
     of each cell. Reads as stronger without being stronger. */
  function buildBuzz(frames) {
    if (!frames || !frames.length) return null;
    var f = frames[0] || {};
    /* `f.intensity || 60` was the real bug here: 0 is a valid, meant
       value ("nothing physical about this line"), but `||` treats it
       as missing and silently promotes it to 60 — the opposite of
       what the API was telling us. An explicit 0 now skips the buzz
       outright instead of being overridden into one. */
    var raw = typeof f.intensity === 'number' ? f.intensity : 60;
    if (raw <= 0) return null;
    var shape = SHAPES[f.patternId] || 'constant';
    var duty = Math.max(0.2, Math.min(1, raw / 100));
    var out = [], total = 0, i = 0;

    while (total < BUZZ_MS && out.length < 80) {
      if (shape === 'heart') {
        out.push(90, 95, 150, 540);
        total += 875; i++; continue;
      }
      var cell =
        shape === 'slow'   ? 520 :
        shape === 'fast'   ? 170 :
        shape === 'wave'   ? 300 :
        shape === 'step'   ? 340 :
        shape === 'random' ? 140 + Math.round(Math.random() * 260) : 660;

      var d = duty;
      if (shape === 'wave') d = duty * (0.42 + 0.58 * Math.abs(Math.sin(i * 0.62)));
      if (shape === 'step') d = duty * Math.min(1, 0.34 + i * 0.17);

      var on = Math.max(40, Math.round(cell * d));
      out.push(on, Math.max(30, cell - on));
      total += cell; i++;
    }
    return { steps: out, shape: shape, ms: Math.min(total, BUZZ_MS) };
  }

  /* ── 3. The room ───────────────────────────────────── */
  function Room(section) {
    this.root = section;
    this.grid = section.querySelector('.vrai__partners-grid');
    this.panel = section.querySelector('[data-vrai-panel]');
    this.rail = section.querySelector('[data-vrai-rail]');
    this.cards = Array.prototype.slice.call(section.querySelectorAll('.vrai__partner'));
    this.open = false;
    this.card = null;
    this.busy = false;
    this.walled = recall(WALL_KEY) === '1';
    this.buzzOn = false;
    this.buzzTimer = null;

    var d = section.dataset;
    this.cfg = {
      api: (d.vraiApi || '').replace(/\/+$/, ''),
      appType: parseInt(d.vraiAppType, 10) || 3,
      appUrl: d.vraiAppUrl || '',
      androidUrl: d.vraiAndroidUrl || '',
      webUrl: d.vraiWebUrl || 'https://www.velvetrituals.ai'
    };
    this.session = new Session(this.cfg);
    this.bind();
  }

  Room.prototype.bind = function () {
    var self = this;
    this.cards.forEach(function (card, i) {
      var btn = card.querySelector('[data-vrai-talk]');
      if (btn) {
        btn.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          self.enter(i);
        });
      }
    });
    this.root.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape' || !self.open) return;
      /* Escape dismisses the topmost thing, not the whole room. */
      if (!self.hideAside()) self.leave();
    });
  };

  /* ── Opening: the others step aside ────────────────── */
  /* Two beats, not one. They leave, and only then does the room
     open into the space — so the sequence reads as cause and
     effect rather than everything moving at once. Leave() runs this
     same choreography backwards, so the delays are set from here in
     JS rather than left to CSS :nth-child rules — one source of truth
     that both directions can read. */
  Room.prototype.enter = function (index) {
    if (this.open || this.busy) return;
    var self = this;
    var wide = window.matchMedia('(min-width: 1024px)').matches;
    this.busy = true;
    this.card = this.cards[index];
    this.opener = this.card.querySelector('[data-vrai-talk]');

    var others = [];
    this.cards.forEach(function (c, i) { if (i !== index) others.push(c); });

    others.forEach(function (c, n) {
      c.style.transitionDelay = (n * 70) + 'ms';
      c.classList.add('is-leaving');
      c.setAttribute('aria-hidden', 'true');
    });

    setTimeout(function () {
      /* The pointer-tilt handler in vr-ai.js leaves an inline transform
         on whatever the cursor last crossed. Clear it before measuring
         or the FLIP inherits a rotation. */
      self.card.style.transition = 'none';
      self.card.style.transform = '';

      /* FIRST — where she sits before the grid changes */
      var first = self.card.getBoundingClientRect();

      self.cards.forEach(function (c, i) {
        c.classList.add(i === index ? 'is-chosen' : 'is-aside');
      });
      self.grid.classList.add('is-open');
      self.buildRail(index);
      self.panel.hidden = false;
      self.renderShell();

      /* LAST — then invert her back and let her glide into place */
      var last = self.card.getBoundingClientRect();
      var dx = first.left - last.left;
      var dy = first.top - last.top;

      if (wide && !REDUCED && (Math.abs(dx) > 1 || Math.abs(dy) > 1)) {
        self.card.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        self.card.getBoundingClientRect();
        self.card.style.transition = 'transform 620ms cubic-bezier(0.16,1,0.3,1)';
        self.card.style.transform = '';
      } else {
        self.card.style.transition = '';
      }

      requestAnimationFrame(function () { self.panel.classList.add('is-in'); });
      self.open = true;

      setTimeout(function () {
        self.card.style.transition = '';
        self.card.style.transform = '';
        others.forEach(function (c) { c.style.transitionDelay = ''; });
        self.busy = false;
        var input = self.panel.querySelector('[data-vrai-input]');
        if (input && wide) input.focus();
      }, REDUCED ? 0 : 660);

      self.start();
    }, REDUCED ? 0 : 300);
  };

  /* The theme blocks carry portrait video but no still, so the face for
     the avatar comes from the character's own record. Monogram shows
     first and the photograph replaces it when it lands. */
  Room.prototype.faceFor = function (card) {
    var known = card.getAttribute('data-vrai-avatar');
    if (known) return Promise.resolve(known);
    if (card.__vrFace) return card.__vrFace;

    var id = card.getAttribute('data-vrai-cid');
    if (!id) return Promise.resolve('');

    card.__vrFace = this.session.call('/connect/character-info/' + id).then(function (r) {
      var imgs = r.body && r.body.data && r.body.data.defaultImages;
      var url = imgs && imgs.length ? imgs[0].imageUrl : '';
      if (url) card.setAttribute('data-vrai-avatar', url);
      return url;
    }).catch(function () { return ''; });
    return card.__vrFace;
  };

  Room.prototype.paintFace = function (card, slot) {
    this.faceFor(card).then(function (url) {
      if (!url || !slot.isConnected) return;
      var img = new Image();
      img.onload = function () {
        slot.textContent = '';
        img.alt = '';
        slot.appendChild(img);
      };
      img.src = url;
    });
  };

  Room.prototype.buildRail = function (index) {
    var self = this;
    this.rail.innerHTML = '';
    this.cards.forEach(function (c, i) {
      if (i === index) return;
      var name = c.getAttribute('data-vrai-name') || '';
      var img = c.getAttribute('data-vrai-avatar') || '';
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'vrai__chip';
      chip.style.setProperty('--d', (i * 70) + 'ms');
      chip.setAttribute('aria-label', 'Meet ' + name + ' in the app');
      chip.innerHTML =
        (img
          ? '<img src="' + img + '" alt="" loading="lazy" width="30" height="30">'
          : '<span class="vrai__chip-mono">' + (name.charAt(0) || '·') + '</span>') +
        '<span class="vrai__chip-name">' + name + '</span>';
      chip.addEventListener('click', function () { self.showAside(c, name); });
      self.rail.appendChild(chip);
      if (!img) self.paintFace(c, chip.querySelector('.vrai__chip-mono'));
    });
  };

  /* Switching partners would spend the same shared allowance, so the
     chips are an invitation out — but never a trap. This sits over the
     conversation and gets out of the way on any tap. */
  Room.prototype.showAside = function (card, name) {
    var self = this;
    this.hideAside();

    var avatar = card.getAttribute('data-vrai-avatar') || '';
    var here = this.card.getAttribute('data-vrai-name') || 'her';
    var aside = document.createElement('div');
    aside.className = 'vrai__aside';
    aside.setAttribute('role', 'dialog');
    aside.setAttribute('aria-label', name + ' is in the app');
    aside.innerHTML =
      '<div class="vrai__aside-card">' +
        '<span class="vrai__aside-face">' +
          (avatar ? '<img src="' + avatar + '" alt="" width="52" height="52">' : (name.charAt(0) || '·')) +
        '</span>' +
        '<p class="vrai__aside-h">' + name + ' is in the app.</p>' +
        '<p class="vrai__aside-p">The website gives you one conversation. The app doesn’t work that way.</p>' +
        '<div class="vrai__aside-acts">' + this.ctas() + '</div>' +
        '<button type="button" class="vrai__aside-back" data-vrai-aside-back>Back to ' + here + '</button>' +
      '</div>';

    /* Any tap that is not one of the two links puts them back in the
       conversation they were already having. */
    aside.addEventListener('click', function (ev) {
      if (ev.target.closest('a')) return;
      self.hideAside();
    });

    this.panel.appendChild(aside);
    if (!avatar) this.paintFace(card, aside.querySelector('.vrai__aside-face'));
    requestAnimationFrame(function () { aside.classList.add('in'); });
    var back = aside.querySelector('[data-vrai-aside-back]');
    if (back) back.focus();
  };

  Room.prototype.hideAside = function () {
    var aside = this.panel.querySelector('.vrai__aside');
    if (!aside) return false;
    aside.classList.remove('in');
    setTimeout(function () { if (aside.parentNode) aside.remove(); }, REDUCED ? 0 : 240);
    var input = this.panel.querySelector('[data-vrai-input]');
    if (input && !input.disabled) input.focus();
    return true;
  };

  /* Closing runs the opening choreography backwards rather than just
     collapsing: the panel folds with its arrival stagger reversed, and
     the chosen card FLIPs back to her resting slot the same way she
     glided out of it — instead of the grid snapping her there instantly
     while the panel is still mid-collapse. */
  Room.prototype.leave = function () {
    if (!this.open || this.busy) return;
    var self = this;
    var wide = window.matchMedia('(min-width: 1024px)').matches;
    this.busy = true;
    this.hideAside();
    this.stopBuzz();

    var others = [];
    this.cards.forEach(function (c) { if (c !== self.card) others.push(c); });

    /* Fold the panel — same transition, run the other way, peeling the
       header/stream/foot off in the reverse of the order they arrived. */
    this.panel.classList.remove('is-in');
    var kids = this.panel.children;
    for (var k = 0; k < kids.length; k++) {
      kids[k].style.transitionDelay = ((kids.length - 1 - k) * 60) + 'ms';
    }

    /* Clear any residual tilt transform before FIRST is measured — same
       precaution enter() takes, so the FLIP doesn't inherit a rotation. */
    this.card.style.transition = 'none';
    this.card.style.transform = '';
    var first = wide ? this.card.getBoundingClientRect() : null;

    this.grid.classList.remove('is-open');
    this.card.classList.remove('is-chosen');
    others.forEach(function (c, n) {
      c.classList.remove('is-aside');
      c.classList.add('is-leaving');
      c.style.transition = 'none';
      c.style.transitionDelay = ((others.length - 1 - n) * 70) + 'ms';
    });

    /* One flush covers every style change made since transition:none —
       the FLIP invert below reads real post-layout geometry, and the
       others' return is now armed to animate on the next frame. */
    this.card.getBoundingClientRect();

    if (wide && !REDUCED) {
      var last = this.card.getBoundingClientRect();
      var dx = first.left - last.left;
      var dy = first.top - last.top;
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        this.card.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        this.card.getBoundingClientRect();
        this.card.style.transition = 'transform 620ms cubic-bezier(0.16,1,0.3,1)';
        this.card.style.transform = '';
      } else {
        this.card.style.transition = '';
      }
    } else {
      this.card.style.transition = '';
    }

    others.forEach(function (c) { c.style.transition = ''; });
    requestAnimationFrame(function () {
      others.forEach(function (c) { c.classList.remove('is-leaving'); });
    });

    this.card.removeAttribute('aria-hidden');
    others.forEach(function (c) { c.removeAttribute('aria-hidden'); });

    setTimeout(function () {
      self.panel.hidden = true;
      self.card.style.transition = '';
      self.card.style.transform = '';
      others.forEach(function (c) {
        c.style.transition = '';
        c.style.transitionDelay = '';
        c.classList.remove('is-leaving');
      });
      for (var k2 = 0; k2 < self.panel.children.length; k2++) {
        self.panel.children[k2].style.transitionDelay = '';
      }
      self.rail.innerHTML = '';
      self.open = false;
      self.busy = false;
      if (self.opener) self.opener.focus();
    }, REDUCED ? 0 : 680);
  };

  /* ── Panel shell ───────────────────────────────────── */
  Room.prototype.renderShell = function () {
    var c = this.card;
    var name = c.getAttribute('data-vrai-name') || 'Her';
    var avatar = c.getAttribute('data-vrai-avatar') || '';

    this.panel.innerHTML =
      '<header class="vrai__room-head">' +
        '<span class="vrai__room-face">' +
          (avatar ? '<img src="' + avatar + '" alt="" width="36" height="36">' : (name.charAt(0) || '·')) +
        '</span>' +
        '<span class="vrai__room-id">' +
          '<b>' + name + '</b>' +
          '<i data-vrai-status>online</i>' +
        '</span>' +
        '<button type="button" class="vrai__room-x" data-vrai-close aria-label="Close the conversation">' +
          '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</header>' +
      '<div class="vrai__room-stream" data-vrai-stream role="log" aria-live="polite" aria-label="Conversation"></div>' +
      '<div class="vrai__room-foot" data-vrai-foot>' +
        '<form class="vrai__room-form" data-vrai-form>' +
          '<input type="text" class="vrai__room-input" data-vrai-input autocomplete="off" ' +
                 'placeholder="Say anything…" aria-label="Write a message" maxlength="400">' +
          '<button type="submit" class="vrai__room-send" data-vrai-send aria-label="Send">' +
            '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</button>' +
        '</form>' +
        (CAN_BUZZ ?
          '<div class="vrai__room-meter">' +
            '<button type="button" class="vrai__buzz" data-vrai-buzz aria-pressed="false">' +
              '<span class="vrai__buzz-dot"></span>Feel it' +
            '</button>' +
          '</div>' +
          '<p class="vrai__buzz-note" data-vrai-buzznote hidden>Your phone, not the toy. The toy does this properly.</p>'
        : '') +
      '</div>';

    var self = this;
    if (!avatar) this.paintFace(c, this.panel.querySelector('.vrai__room-face'));
    this.panel.querySelector('[data-vrai-close]').addEventListener('click', function () { self.leave(); });
    this.panel.querySelector('[data-vrai-form]').addEventListener('submit', function (ev) {
      ev.preventDefault();
      var input = self.panel.querySelector('[data-vrai-input]');
      var text = (input.value || '').trim();
      if (!text) return;
      input.value = '';
      self.send(text);
    });

    var buzz = this.panel.querySelector('[data-vrai-buzz]');
    if (buzz) {
      buzz.addEventListener('click', function () {
        self.buzzOn = !self.buzzOn;
        buzz.classList.toggle('is-on', self.buzzOn);
        buzz.setAttribute('aria-pressed', String(self.buzzOn));
        var note = self.panel.querySelector('[data-vrai-buzznote]');
        if (note) note.hidden = !self.buzzOn;
        if (!self.buzzOn) self.stopBuzz();
        else if (navigator.vibrate) navigator.vibrate(18); /* confirm the grant */
      });
    }
  };

  /* ── Stream primitives ─────────────────────────────── */
  Room.prototype.stream = function () {
    return this.panel.querySelector('[data-vrai-stream]');
  };
  Room.prototype.toBottom = function () {
    var s = this.stream();
    if (s) s.scrollTop = s.scrollHeight;
  };

  Room.prototype.bubble = function (side, text) {
    var b = document.createElement('div');
    b.className = 'vrai__b vrai__b--' + side;
    b.textContent = text || '';
    this.stream().appendChild(b);
    requestAnimationFrame(function () { b.classList.add('in'); });
    this.toBottom();
    return b;
  };

  Room.prototype.dots = function (on) {
    var existing = this.panel.querySelector('.vrai__dots');
    if (!on) { if (existing) existing.remove(); return; }
    if (existing) return existing;
    var d = document.createElement('div');
    d.className = 'vrai__dots';
    d.innerHTML = '<span></span><span></span><span></span>';
    this.stream().appendChild(d);
    this.toBottom();
    return d;
  };

  /* Replies arrive whole — there is no streaming in the stack — so the
     pacing is ours. Even delivery beats a token stream's stutter. */
  Room.prototype.reveal = function (el, text, done) {
    var self = this;
    if (REDUCED) { el.textContent = text; this.toBottom(); done && done(); return; }
    var parts = text.split(/(\s+)/);
    var i = 0;
    (function tick() {
      var chunk = '';
      for (var n = 0; n < 6 && i < parts.length; n++, i++) chunk += parts[i];
      el.textContent += chunk;
      self.toBottom();
      if (i < parts.length) setTimeout(tick, 45);
      else done && done();
    })();
  };

  Room.prototype.note = function (text, kind) {
    var n = document.createElement('p');
    n.className = 'vrai__note' + (kind ? ' is-' + kind : '');
    n.textContent = text;
    this.stream().appendChild(n);
    this.toBottom();
    return n;
  };

  /* ── Conversation ──────────────────────────────────── */

  /* send-message refuses with "Conversation Id not found" until the
     thread exists, and GET /conversation is what creates it — so this
     call is a precondition, not a greeting fetch. Its body is ignored
     on purpose: the opening line is already on the card and renders
     with no network at all. Once a thread exists it is never called
     again, which is why it is gated on empty history. */
  Room.prototype.ensureThread = function (id) {
    if (this.thread) return this.thread;
    this.thread = this.session.call('/connect/conversation/' + id);
    return this.thread;
  };

  Room.prototype.start = function () {
    var self = this;
    var opening = this.card.getAttribute('data-vrai-opening');
    var id = this.card.getAttribute('data-vrai-cid');
    this.thread = null;
    this.dots(true);

    this.session.call('/connect/chat-history/' + id + '?page=1&limit=30')
      .then(function (r) {
        var rows = (r.body && r.body.data) || [];
        self.dots(false);

        if (rows.length) {
          /* The guest token lives 30 days and the backend keeps the
             thread against it, so a returning visitor picks up where
             they left off rather than meeting her again. */
          self.replay(rows);
          if (self.walled) self.showWall('You were here before.', null, false);
          return;
        }

        self.ensureThread(id);          /* first visit — create it now */
        setTimeout(function () {
          var b = self.bubble('ai', '');
          self.reveal(b, clean(opening || 'Hey. You found me.'), function () {
            /* Walled but no history for HER specifically means the limit
               was spent on someone else — "you've met her already" would
               be a lie here. */
            if (self.walled) self.showWall('You’re out of turns for now.', null, false);
          });
        }, REDUCED ? 0 : 520);
      })
      .catch(function () {
        self.dots(false);
        self.note('Could not reach her just now. Try again in a moment.', 'bad');
      });
  };

  /* History comes back newest-first; the room reads oldest-first. */
  Room.prototype.replay = function (rows) {
    var stream = this.stream();
    var opening = this.card.getAttribute('data-vrai-opening');
    var ordered = rows.slice().reverse();

    ordered.forEach(function (m, i) {
      var b = document.createElement('div');
      b.className = 'vrai__b vrai__b--' + (m.sender === 'user' ? 'user' : 'ai') + ' in';
      /* The thread opens with whatever greeting the server stored when
         it was created, but the visitor saw the card's line. Show them
         back the conversation they remember having. */
      b.textContent = (i === 0 && m.sender === 'ai' && opening)
        ? opening
        : clean(m.message);
      stream.appendChild(b);
    });
    this.note('Picked up where you left off', 'quiet');
    this.toBottom();
  };

  Room.prototype.setBusy = function (on) {
    var input = this.panel.querySelector('[data-vrai-input]');
    var send = this.panel.querySelector('[data-vrai-send]');
    if (input) input.disabled = on;
    if (send) send.disabled = on;
    var status = this.panel.querySelector('[data-vrai-status]');
    if (status) status.textContent = on ? 'typing…' : 'online';
  };

  Room.prototype.send = function (text) {
    if (this.walled) return;
    var self = this;
    var id = this.card.getAttribute('data-vrai-cid');

    this.bubble('user', text);
    this.setBusy(true);
    this.dots(true);

    this.ensureThread(id).then(function () {
      return self.session.call('/connect/send-message/' + id, {
        method: 'POST',
        body: { message: text }             /* the gateway rejects any extra key */
      });
    }).then(function (r) {
      self.dots(false);

      if (r.status === 403 && r.body && r.body.code === 'GUEST_LIMIT_REACHED') {
        self.walled = true;
        store(WALL_KEY, '1');
        self.showWall(null, null, true);   /* landed live — blur her last line */
        return;
      }
      if (r.status === 403 && r.body && r.body.code === 'CHARACTER_LOCKED') {
        self.setBusy(false);
        self.note('She is not open on the web. The app has all of them.', 'bad');
        return;
      }
      if (r.status === 429) {
        self.setBusy(false);
        self.note('That is the limit for today. It resets tomorrow.', 'bad');
        return;
      }
      /* A missing thread comes back as 201 with the failure buried in
         data.error, so status alone is not proof of a reply. */
      var data = r.body && r.body.data;
      if (!r.ok || !data || data.error || !data.response) {
        self.setBusy(false);
        self.thread = null;               /* rebuild it on the retry */
        self.note('She dropped off for a second. Send that again.', 'bad');
        return;
      }

      var reply = clean(data.response);
      var turns = splitTurns(reply);

      self.play(turns, 0, function () {
        self.setBusy(false);
        if (data.imageSuggestion || data.mediaUrl) self.showImageGate();
        if (data.pattern && data.pattern.length) self.offerBuzz(data.pattern);
      });
    }).catch(function () {
      self.dots(false);
      self.setBusy(false);
      self.note('Connection dropped. Send that again.', 'bad');
    });
  };

  Room.prototype.play = function (turns, i, done) {
    var self = this;
    if (i >= turns.length) { done(); return; }
    var b = this.bubble('ai', '');
    this.reveal(b, turns[i], function () {
      if (i + 1 < turns.length) {
        self.dots(true);
        setTimeout(function () {
          self.dots(false);
          self.play(turns, i + 1, done);
        }, REDUCED ? 0 : 700);
      } else done();
    });
  };

  /* ── 5. Gates ──────────────────────────────────────── */

  /* Pictures belong behind an account. The ask itself is the cue. */
  Room.prototype.showImageGate = function () {
    var card = document.createElement('div');
    card.className = 'vrai__gate';
    card.innerHTML =
      '<p class="vrai__gate-line">She wants to show you something.</p>' +
      '<div class="vrai__gate-acts">' +
        this.ctas() +
      '</div>';
    this.stream().appendChild(card);
    requestAnimationFrame(function () { card.classList.add('in'); });
    this.toBottom();
  };

  Room.prototype.ctas = function () {
    var appHref = this.cfg.appUrl || this.cfg.webUrl;
    var isAndroid = /android/i.test(navigator.userAgent || '');
    if (isAndroid && this.cfg.androidUrl) appHref = this.cfg.androidUrl;
    return '<a class="vrai__btn vrai__btn--solid" href="' + appHref + '" target="_blank" rel="noopener noreferrer">Get Velvet Vibes</a>' +
           '<a class="vrai__btn vrai__btn--ghost" href="' + this.cfg.webUrl + '?utm_source=shopify&utm_medium=ai-demo" target="_blank" rel="noopener noreferrer">velvetrituals.ai</a>';
  };

  /* `cut` blurs her closing line. That belongs only to the moment the
     wall actually lands mid-conversation — the unfinished sentence is
     the hook. Replaying a thread the visitor already read and fogging
     the last thing they saw just looks like the page broke, so the
     returning paths pass it false.

     No copy anywhere states the number of free messages — the wall
     itself, arriving when it arrives, is the only disclosure. */
  Room.prototype.showWall = function (title, line, cut) {
    var foot = this.panel.querySelector('[data-vrai-foot]');
    if (!foot || foot.querySelector('.vrai__wall')) return;

    if (cut) {
      /* :last-of-type keys off the tag, and every bubble is a div — so
         ask for all of hers and take the final one. */
      var hers = this.stream().querySelectorAll('.vrai__b--ai');
      if (hers.length) hers[hers.length - 1].classList.add('is-cut');
    }

    this.stopBuzz();
    var wall = document.createElement('div');
    wall.className = 'vrai__wall';
    wall.innerHTML =
      '<p class="vrai__wall-h">' + (title || 'She’s mid-sentence.') + '</p>' +
      '<p class="vrai__wall-p">' + (line || 'That’s the website’s limit. The app doesn’t have one.') + '</p>' +
      '<div class="vrai__wall-acts">' + this.ctas() + '</div>';
    foot.appendChild(wall);
    requestAnimationFrame(function () { wall.classList.add('in'); });

    foot.classList.add('is-walled');
    this.setBusy(true);
    var status = this.panel.querySelector('[data-vrai-status]');
    if (status) { status.textContent = 'in the app'; status.classList.add('is-gone'); }
    this.toBottom();
  };

  /* ── Buzz: tap-gated, on purpose ────────────────────── */
  /* A reply's pattern only exists once the network round-trip and the
     paced reveal both finish — several seconds after the Send tap that
     started them. Firing navigator.vibrate() from that callback chain
     is why "Feel it" never reliably did anything: the browser only
     honours the call from inside a fresh user gesture, and by then the
     one that triggered this whole exchange is long gone. So it never
     auto-fires. A small chip appears under a reply that carries a
     pattern, and tapping IT — a real, current gesture — is what calls
     vibrate(), every time. */
  Room.prototype.offerBuzz = function (frames) {
    if (!this.buzzOn || !CAN_BUZZ) return;
    var plan = buildBuzz(frames);
    if (!plan) return;
    var self = this;

    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'vrai__feel';
    chip.setAttribute('aria-live', 'polite');
    chip.innerHTML =
      '<span class="vrai__feel-ring" aria-hidden="true"></span>' +
      '<span class="vrai__feel-label">Feel this</span>';
    this.stream().appendChild(chip);
    requestAnimationFrame(function () { chip.classList.add('in'); });
    this.toBottom();

    chip.addEventListener('click', function onTap() {
      chip.removeEventListener('click', onTap);
      var ok = navigator.vibrate(plan.steps);
      if (!ok) {
        chip.classList.add('is-failed');
        var ring = chip.querySelector('.vrai__feel-ring');
        if (ring) ring.remove();
        chip.querySelector('.vrai__feel-label').textContent =
          'Didn’t buzz — check your phone’s vibration setting';
        return;
      }
      self.playBuzz(chip, plan);
    });
  };

  Room.prototype.playBuzz = function (chip, plan) {
    var self = this;
    chip.classList.add('is-live');
    chip.disabled = true;

    var bars = '';
    for (var i = 0; i < plan.steps.length && i < 40; i += 2) {
      var cell = plan.steps[i] + plan.steps[i + 1];
      var h = Math.round((plan.steps[i] / cell) * 100);
      bars += '<i style="height:' + Math.max(14, h) + '%"></i>';
    }
    var ring = chip.querySelector('.vrai__feel-ring');
    if (ring) ring.remove();
    var wave = document.createElement('span');
    wave.className = 'vrai__feel-wave';
    wave.innerHTML = bars + '<u></u>';
    chip.insertBefore(wave, chip.firstChild);
    chip.querySelector('.vrai__feel-label').textContent = 'Feeling it…';

    var head = wave.querySelector('u');
    head.style.setProperty('--run', plan.ms + 'ms');
    head.style.setProperty('--w', wave.clientWidth + 'px');
    requestAnimationFrame(function () { wave.classList.add('is-live'); });

    this.buzzTimer = setTimeout(function () {
      self.buzzTimer = null;
      navigator.vibrate(0);
      chip.classList.add('is-done');
      setTimeout(function () { if (chip.parentNode) chip.remove(); }, 420);
    }, plan.ms + 120);
  };

  Room.prototype.stopBuzz = function () {
    if (this.buzzTimer) { clearTimeout(this.buzzTimer); this.buzzTimer = null; }
    if (CAN_BUZZ) navigator.vibrate(0);
  };

  /* ── Boot ──────────────────────────────────────────── */
  function boot(root) {
    (root || document).querySelectorAll('[data-vrai-room]').forEach(function (section) {
      if (section.__vrAiRoom) return;
      if (!section.dataset.vraiApi) return;
      section.__vrAiRoom = new Room(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
